import type { MatrixItem as MatrixItemType } from '@webforge-survey/shared';
import { ItemShell } from './ItemShell';

interface MatrixItemProps {
  item: MatrixItemType;
  value: Record<string, string>;
  error?: string;
  onChange: (value: Record<string, string>) => void;
}

/**
 * matrix : grille Likert.
 *
 * - Desktop : <table> sémantique. Chaque ligne est un radiogroup, chaque
 *   cellule contient une radio dont le name correspond à la ligne. L'en-tête
 *   d'échelle (thead) sert d'`aria-describedby` aux radios via id stables.
 * - Mobile (< sm) : la table est remplacée par une carte par ligne avec un
 *   fieldset interne (chaque ligne = radiogroup) — bien plus accessible que
 *   d'écraser une matrice large en très petit.
 *
 * "Ligne 3 sur 8" annoncé via la balise <caption> + sr-only inline.
 */
export function MatrixItem({ item, value, error, onChange }: MatrixItemProps) {
  const setRow = (rowName: string, val: string) => {
    onChange({ ...value, [rowName]: val });
  };

  const headerId = `matrix-${item.name}-header`;
  const total = item.rows.length;

  return (
    <ItemShell
      name={item.name}
      label={item.label}
      {...(item.hint !== undefined && { hint: item.hint })}
      {...(item.required !== undefined && { required: item.required })}
      {...(error !== undefined && { error })}
    >
      {/* Desktop : table sémantique */}
      <div className="hidden sm:block overflow-x-auto -mx-2 px-2">
        <table className="w-full border-collapse">
          <caption className="sr-only">
            Grille de {total} lignes. Sélectionnez une valeur par ligne sur
            l'échelle de {item.choices.length} options.
          </caption>
          <thead id={headerId}>
            <tr>
              <th scope="col" className="text-left p-3 text-sm text-text-muted font-normal">
                Énoncé
              </th>
              {item.choices.map((choice) => (
                <th
                  scope="col"
                  key={choice.value}
                  className="p-3 text-sm text-text-muted font-normal text-center min-w-[5rem]"
                >
                  {choice.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {item.rows.map((row, idx) => {
              const groupId = `${item.name}-${row.name}-group`;
              return (
                <tr
                  key={row.name}
                  className={idx % 2 === 0 ? 'bg-surface' : 'bg-surface-2'}
                >
                  <th
                    id={groupId}
                    scope="row"
                    className="text-left p-3 font-normal text-text align-top w-2/5"
                  >
                    <span className="sr-only">
                      Ligne {idx + 1} sur {total}.
                    </span>
                    {row.label}
                  </th>
                  {item.choices.map((choice) => {
                    const id = `${item.name}-${row.name}-${choice.value}`;
                    const checked = value[row.name] === choice.value;
                    return (
                      <td key={choice.value} className="p-3 text-center align-middle">
                        <label htmlFor={id} className="inline-flex items-center justify-center w-full cursor-pointer">
                          <span className="sr-only">
                            {row.label} — {choice.label}
                          </span>
                          <input
                            id={id}
                            type="radio"
                            name={`${item.name}-${row.name}`}
                            value={choice.value}
                            checked={checked}
                            onChange={() => setRow(row.name, choice.value)}
                            className="h-5 w-5 accent-accent"
                            aria-labelledby={groupId}
                          />
                        </label>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile : card par ligne, radiogroup classique */}
      <div className="sm:hidden space-y-4">
        {item.rows.map((row, idx) => {
          const groupId = `${item.name}-${row.name}-group-mobile`;
          return (
            <fieldset
              key={row.name}
              role="radiogroup"
              aria-labelledby={groupId}
              className="card !p-4"
            >
              <legend id={groupId} className="text-base font-medium text-text mb-3">
                <span className="text-xs text-text-muted block mb-1">
                  Ligne {idx + 1} sur {total}
                </span>
                {row.label}
              </legend>
              <div className="space-y-2">
                {item.choices.map((choice) => {
                  const id = `${item.name}-${row.name}-${choice.value}-m`;
                  const checked = value[row.name] === choice.value;
                  return (
                    <label
                      key={choice.value}
                      htmlFor={id}
                      className={`option-card ${checked ? 'option-card-selected' : ''}`}
                    >
                      <input
                        id={id}
                        type="radio"
                        name={`${item.name}-${row.name}-m`}
                        value={choice.value}
                        checked={checked}
                        onChange={() => setRow(row.name, choice.value)}
                        className="mt-1 h-5 w-5 accent-accent flex-shrink-0"
                      />
                      <span>{choice.label}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          );
        })}
      </div>
    </ItemShell>
  );
}
